import { useNetworkDashboardKpis, useRealEstateKpis, useStoreProjects } from '@gozoki/api'
import { createFileRoute } from '@tanstack/react-router'
import { OpenStoresCountCardLink } from '~/components/dashboards/open-stores-count-card'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import type { RealEstateProject } from '~/components/routes/development/real-estate/store-project/types'
import { CACard } from '~/components/routes/network-dashboard/cards/ca-card'
import { CACardDetails } from '~/components/routes/network-dashboard/cards/ca-card-details'
import { CategoriesCard } from '~/components/routes/network-dashboard/cards/categories-card'
import { CustomerSatifsactionCard } from '~/components/routes/network-dashboard/cards/customer-satifsaction-card'
import { HRCard } from '~/components/routes/network-dashboard/cards/hr-card'
import { NetworkStatCard } from '~/components/routes/network-dashboard/cards/network-stat-card'
import { ProductsCard } from '~/components/routes/network-dashboard/cards/products-card'
import { RealEstateCostsCard } from '~/components/routes/network-dashboard/cards/real-estate-costs-card'
import { UpcomingStoresCard } from '~/components/routes/network-dashboard/cards/upcoming-stores-card'
import { getUpcomingStores } from '~/utils/new-dashboards/upcoming-stores'

function NetworkDashboardPage() {
  const { data: projects = [] } = useStoreProjects()
  const { data: realEstateKpis } = useRealEstateKpis()
  const { data: networkKpis } = useNetworkDashboardKpis()

  const dailyAggregates = networkKpis?.dailyAggregates ?? []
  const storeWeeklyCas = networkKpis?.storeWeeklyCas ?? []
  const categoryPdm = networkKpis?.categoryPdm ?? []
  const topProducts = networkKpis?.topProducts ?? []

  const midpoint = Math.ceil(dailyAggregates.length / 2)
  const currentWeekAggregates = dailyAggregates.slice(midpoint)
  const previousWeekAggregates = dailyAggregates.slice(0, midpoint)
  const totalCaTTC = currentWeekAggregates.reduce((sum, a) => sum + a.totalCa, 0)
  const previousCaTTC = previousWeekAggregates.reduce((sum, a) => sum + a.totalCa, 0)
  const caTTCPercentageChange =
    previousCaTTC === 0 ? 0 : Math.round(((totalCaTTC - previousCaTTC) / previousCaTTC) * 100)

  const matureCurrentCaTTC = currentWeekAggregates.reduce((sum, a) => sum + a.matureCa, 0)
  const maturePreviousCaTTC = previousWeekAggregates.reduce((sum, a) => sum + a.matureCa, 0)
  const matureCurrentCartsCount = currentWeekAggregates.reduce(
    (sum, a) => sum + a.matureCartsCount,
    0,
  )
  const maturePreviousCartsCount = previousWeekAggregates.reduce(
    (sum, a) => sum + a.matureCartsCount,
    0,
  )
  const matureCurrentProductsSold = currentWeekAggregates.reduce(
    (sum, a) => sum + a.matureProductsSold,
    0,
  )

  const matureCAPerStore = currentWeekAggregates.reduce(
    (sum, a) => sum + (a.matureStoreCount > 0 ? a.matureCa / a.matureStoreCount : 0),
    0,
  )

  const hrMetrics = networkKpis?.hrMetrics
  const hrPercentageCA =
    hrMetrics && matureCAPerStore > 0
      ? Math.round((hrMetrics.avgSalaryCostPerStore / matureCAPerStore) * 100)
      : 0

  const panierMoyen =
    matureCurrentCartsCount === 0 ? 0 : matureCurrentCaTTC / matureCurrentCartsCount
  const previousPanierMoyen =
    maturePreviousCartsCount === 0 ? 0 : maturePreviousCaTTC / maturePreviousCartsCount
  const panierMoyenPercentageChange =
    previousPanierMoyen === 0
      ? 0
      : Math.round(((panierMoyen - previousPanierMoyen) / previousPanierMoyen) * 100)

  const articlesPerCart =
    matureCurrentCartsCount === 0 ? 0 : matureCurrentProductsSold / matureCurrentCartsCount

  const delta = realEstateKpis?.openStoresDelta30d ?? null
  const openStoresDeltaIndex = delta != null ? `${delta >= 0 ? '+' : ''}${delta} vs M-1` : undefined
  const typedProjects = projects as RealEstateProject[]
  const openStoresCount = typedProjects.filter((p) => p.status === 'open' && !p.isAbandoned).length
  const today = new Date()

  const upcomingStores = getUpcomingStores(typedProjects).filter(
    (p) =>
      today <= p.openingDate &&
      p.openingDate <= new Date(today.getTime() + 3 * 3_600_000 * 24 * 30),
  )
  //   const openStoresDeltaIndex = realEstateKpis?.openStoresDelta30d ?? null

  return (
    <>
      <AppHeader className="mb-4">
        <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
          Réseau & Boutiques
        </Title>
      </AppHeader>

      <AppContainer>
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: '16rem minmax(0, 1fr) minmax(10rem, 15rem)' }}
        >
          {/* Left column */}
          <div className="max-h-0 min-h-full flex flex-col gap-2">
            <OpenStoresCountCardLink
              openStoresCount={openStoresCount}
              openStoresDeltaIndex={openStoresDeltaIndex}
            />
            <UpcomingStoresCard upcomingStores={upcomingStores} />
            <RealEstateCostsCard />
            <CustomerSatifsactionCard customerSatifsaction={4.6} />
          </div>

          {/* Center column */}
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 gap-2">
              <div className="flex-1 min-w-0">
                <CACard variant="network" aggregates={dailyAggregates} />
              </div>
              <div className="flex-1 min-w-0">
                <CACard variant="store" aggregates={dailyAggregates} />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-1 min-w-0">
                <CategoriesCard categories={categoryPdm} />
              </div>
              <div className="flex-1 min-w-0">
                <ProductsCard products={topProducts} categories={categoryPdm} />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-0.3 flex">
                <HRCard
                  avgETP={hrMetrics?.avgETP ?? 0}
                  percentageCA={hrPercentageCA}
                  percentageInPerson={hrMetrics?.inPersonHoursTotal ?? 0}
                  percentageRemote={hrMetrics?.remoteHoursTotal ?? 0}
                />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                <NetworkStatCard
                  title="Tx de casse"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                />
                <NetworkStatCard
                  title="Tx de vol"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={-10}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                />
                <NetworkStatCard
                  title="Autres charges"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="down"
                  comparisonText="vs S-1"
                />
                <NetworkStatCard
                  title="Tx de service"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                />
                <NetworkStatCard
                  title="Panier moyen"
                  value={`${Number(panierMoyen.toFixed(2)).toLocaleString('fr-FR')} €`}
                  subText={`${articlesPerCart.toFixed(1)} A/T`}
                  comparisonPercentage={panierMoyenPercentageChange}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                />
                <NetworkStatCard
                  variant="primary-light"
                  title="Contribution"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText="vs S-1"
                />
              </div>
            </div>
          </div>

          {/* Right column — max-h-0 + min-h-full prevents this cell from
             influencing the grid row height while still stretching to fill it */}
          <div className="max-h-0 min-h-full">
            <CACardDetails
              caTTC={totalCaTTC}
              caTTCPercentageChange={caTTCPercentageChange}
              storeWeeklyCas={storeWeeklyCas}
            />
          </div>
        </div>
      </AppContainer>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/network-dashboard/')({
  component: NetworkDashboardPage,
})
